// Mailbox.jsx
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { Box, Grid, Typography } from "@mui/material";

import api from "../api/axios";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";

import { UserContext } from "../Components/Context/Context";
import SidebarMailbox from "./components/SidebarMailbox";
import FiltersBar from "./components/FiltersBar";
import MessageViewer from "./components/MessageViewer";
import MessageComposer from "./components/MessageComposer";

import { useRichTextEditor } from "./components/Editor";

export default function Mailbox({
  dataCase,
  heighProp = false,
  insideCase = false,
}) {
  const { userData } = useContext(UserContext);
  const user = userData?.user || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const messageIdFromQuery = Number(searchParams.get("message_id") || 0);

  // =====================
  // ESTADOS (SIN CAMBIOS)
  // =====================
  const [messages, setMessages] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);

  const [companies, setCompanies] = useState([]);
  const [associatedOptions, setAssociatedOptions] = useState([]);

  const [sendMode, setSendMode] = useState("lawyer");
  const [filter, setFilter] = useState(insideCase ? "sent" : "inbox");
  const [selected, setSelected] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [menuScopeFilter, setMenuScopeFilter] = useState("all");

  const [newMsg, setNewMsg] = useState({ recipients: [], subject: "" });

  const [paginationInbox, setPaginationInbox] = useState({
    page: 1,
    perPage: 10,
    last_page: 1,
    total: 0,
  });

  const [paginationSent, setPaginationSent] = useState({
    page: 1,
    perPage: 10,
    last_page: 1,
    total: 0,
  });

  const [sharedFilters, setSharedFilters] = useState({
    company_id: "",
    lawyer_id: "",
    subject: "",
    date_from: "",
    date_to: "",
    status: "",
  });

  const [applyToggle, setApplyToggle] = useState(false);
  const sourceContext = insideCase ? "casetabssection" : "menuinbox";
  const typingTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const latestGetMessagesRequestRef = useRef(0);

  const { editorState, onEditorChange, clearEditor, getHtmlFromEditor } =
    useRichTextEditor();

  const handleEditorChange = (state) => {
    onEditorChange(state);
    setIsTyping(true);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 600);
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const normalizeRecipientOption = (item, type) => {
    if (!item?.id) return null;

    return {
      ...item,
      id: item.id,
      type,
      name: item.name || "",
      surname: item.surname || "",
      email: item.email || "",
      company_id: item.company_id || null,
    };
  };

  const getApiErrorMessage = (error, fallback) => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  };

  const logMailboxError = useCallback(
    ({ scope, endpoint, error, payload }) => {
      const status = error?.response?.status;
      const responseData = error?.response?.data;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Error desconocido";

      console.group(`[Mailbox][${scope}] ${message}`);
      console.error("message:", message);
      console.error("endpoint:", endpoint || "N/A");
      console.error("status:", status || "N/A");
      console.error("source_context:", sourceContext);
      console.error("insideCase:", insideCase);
      console.error("userId:", user?.id || "N/A");
      if (payload) {
        console.error("payload:", payload);
      }
      if (responseData) {
        console.error("responseData:", responseData);
      }
      console.error("stack:", error?.stack || "N/A");
      console.groupEnd();
    },
    [insideCase, sourceContext, user?.id],
  );

  const hasMessageBody = (html = "") => {
    const plain = html
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();

    return plain.length > 0;
  };

  // =====================
  // HELPERS / HANDLERS
  // =====================
  const resetPagesOnFilterChange = () => {
    setPaginationInbox((p) => ({ ...p, page: 1 }));
    setPaginationSent((p) => ({ ...p, page: 1 }));
  };

  const openMessage = async (msg) => {
    setSelected(msg);
    if (msg.status === "unread") {
      if (filter === "inbox") {
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, status: "read" } : m)),
        );
      } else {
        setSentMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, status: "read" } : m)),
        );
      }

      try {
        await api.post(`${process.env.REACT_APP_API_URL}/messages/mark-read`, {
          id: msg.id,
        });
        window.dispatchEvent(new Event("messages:marked-read"));
      } catch (error) {
        logMailboxError({
          scope: "markRead",
          endpoint: `${process.env.REACT_APP_API_URL}/messages/mark-read`,
          error,
          payload: { id: msg.id },
        });
      }
    }
  };

  const handlePageChange = (page) => {
    if (filter === "inbox") {
      setPaginationInbox((p) => (p.page === page ? p : { ...p, page }));
    } else {
      setPaginationSent((p) => (p.page === page ? p : { ...p, page }));
    }
  };

  useEffect(() => {
    if (
      !messageIdFromQuery ||
      !Array.isArray(messages) ||
      messages.length === 0
    ) {
      return;
    }

    const target = messages.find(
      (m) => Number(m?.id || 0) === messageIdFromQuery,
    );
    if (!target) {
      return;
    }

    setFilter("inbox");
    openMessage(target);

    const next = new URLSearchParams(searchParams);
    next.delete("message_id");
    setSearchParams(next, { replace: true });
  }, [messageIdFromQuery, messages]);

  // =====================
  // FETCH MENSAJES (IGUAL)
  // =====================
  const getMessages = useCallback(async () => {
    const requestId = ++latestGetMessagesRequestRef.current;
    setLoadingMessages(true);
    const requestPayload = {
      page: filter === "inbox" ? paginationInbox.page : paginationSent.page,
      objPerPage:
        filter === "inbox" ? paginationInbox.perPage : paginationSent.perPage,
      subject: sharedFilters.subject || false,
      status: sharedFilters.status || false,
      user_id: sharedFilters.lawyer_id || false,
      case_id: dataCase?.id || false,
      inside_flag: !!dataCase?.id,
      source_context: sourceContext,
      menu_scope: insideCase ? "all" : menuScopeFilter,
      date_from: sharedFilters.date_from
        ? dayjs(sharedFilters.date_from, "DD-MM-YYYY").format(
            "YYYY-MM-DD 00:00:00",
          )
        : false,
      date_to: sharedFilters.date_to
        ? dayjs(sharedFilters.date_to, "DD-MM-YYYY").format(
            "YYYY-MM-DD 00:00:00",
          )
        : false,
    };

    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/messages`,
        requestPayload,
      );

      const { messages_to, messages_from, companies: companiesResp } = data;

      if (messages_to) {
        setMessages(messages_to.data || []);
        setPaginationInbox((p) => ({
          ...p,
          last_page: messages_to.last_page,
          total: messages_to.total,
        }));
      }

      if (messages_from) {
        setSentMessages(messages_from.data || []);
        setPaginationSent((p) => ({
          ...p,
          last_page: messages_from.last_page,
          total: messages_from.total,
        }));
      }

      if (companiesResp) {
        setCompanies(companiesResp || []);
      }
    } catch (error) {
      const isStaleRequest = requestId !== latestGetMessagesRequestRef.current;
      const shouldReportError = isMountedRef.current && !isStaleRequest;

      if (!shouldReportError) {
        return;
      }

      logMailboxError({
        scope: "getMessages",
        endpoint: `${process.env.REACT_APP_API_URL}/messages`,
        error,
        payload: requestPayload,
      });
      Swal.fire("Error", "No se pudo obtener mensajes", "error");
    } finally {
      const isCurrentRequest =
        requestId === latestGetMessagesRequestRef.current;

      if (isMountedRef.current && isCurrentRequest) {
        setLoadingMessages(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationInbox.page,
    paginationSent.page,
    applyToggle,
    sharedFilters.subject,
    sharedFilters.status,
    sharedFilters.lawyer_id,
    sharedFilters.date_from,
    sharedFilters.date_to,
    filter,
    menuScopeFilter,
    dataCase?.id,
    insideCase,
    sourceContext,
  ]);

  const sendMessage = async () => {
    const html = getHtmlFromEditor();
    const recipients = newMsg.recipients || [];

    const subject = newMsg.subject?.trim() || "";

    if (!user?.id) {
      console.error(
        "[Mailbox][sendMessage] No se pudo identificar remitente: user.id vacío",
      );
      Swal.fire("Error", "No se pudo identificar al remitente", "error");
      return;
    }

    if (!recipients.length || !subject || !hasMessageBody(html)) {
      console.error("[Mailbox][sendMessage] Campos incompletos", {
        hasRecipients: recipients.length > 0,
        hasSubject: !!subject,
        hasBody: hasMessageBody(html),
      });
      Swal.fire("Campos incompletos", "Completa todos los campos", "warning");
      return;
    }

    const { isConfirmed } = await Swal.fire({
      icon: "question",
      title:
        recipients.length > 1
          ? `¿Enviar ${recipients.length} mensajes?`
          : "¿Enviar mensaje?",
      showCancelButton: true,
      confirmButtonText: "Enviar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    if (!isConfirmed) return;

    setIsSending(true);

    const basePayload = {
      date: new Date().toISOString().slice(0, 10),
      from: user.id,
      subject,
      message: html,
      status: "unread",
      source_context: sourceContext,
      ...(insideCase && {
        case_id: dataCase?.id,
        company_name: dataCase?.company_name,
        case_type_name: dataCase?.case_type_name,
      }),
    };

    Swal.fire({
      title: "Enviando mensaje...",
      text: "Por favor espera",
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      timer: 10000, // seguridad por si algo falla
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const storeUrl = `${process.env.REACT_APP_API_URL}/messages/store`;
      const responses = await Promise.all(
        recipients.map((recipient) =>
          api.post(storeUrl, {
            ...basePayload,
            to: recipient.id,
            to_type: recipient.type,
            email: recipient.email || "",
            usertype: recipient.type || "lawyer",
          }),
        ),
      );

      const hasInvalidResponse = responses.some(
        (res) => !res?.data?.success || !res?.data?.message,
      );
      if (hasInvalidResponse) {
        throw new Error("No se pudo enviar uno o más mensajes");
      }

      // Cerrar loading
      Swal.close();

      const sentMsgs = responses
        .map((res) => res?.data?.message)
        .filter(Boolean);

      setSentMessages((prev) => [...sentMsgs, ...prev]);
      clearEditor();
      setNewMsg((prev) => ({ ...prev, subject: "", recipients: [] }));
      setSelected(sentMsgs[0] || null);
      setFilter("sent");
      await getMessages();

      Swal.fire({
        icon: "success",
        title: "Enviado",
        text: "Mensaje enviado correctamente",
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.close();

      logMailboxError({
        scope: "sendMessage",
        endpoint: `${process.env.REACT_APP_API_URL}/messages/store`,
        error,
        payload: {
          ...basePayload,
          recipients: recipients.map((r) => ({
            id: r.id,
            type: r.type,
            email: r.email || "",
          })),
        },
      });

      Swal.fire({
        icon: "error",
        title: "Error",
        text: getApiErrorMessage(error, "No se pudo enviar el mensaje"),
      });
    } finally {
      setIsSending(false);
    }
  };

  const displayedMessages = filter === "inbox" ? messages : sentMessages;

  useEffect(() => {
    getMessages();
  }, [getMessages]);

  useEffect(() => {
    if (insideCase && filter !== "sent") {
      setFilter("sent");
    }
  }, [insideCase, filter]);

  useEffect(() => {
    if (insideCase) {
      setMenuScopeFilter("all");
    }
  }, [insideCase]);

  useEffect(() => {
    if (insideCase) return;

    setPaginationInbox((prev) => ({ ...prev, page: 1 }));
    setPaginationSent((prev) => ({ ...prev, page: 1 }));
    setSelected(null);
  }, [menuScopeFilter, insideCase]);

  useEffect(() => {
    if (!insideCase) return;

    const caseClient = normalizeRecipientOption(
      dataCase?.clients_relation,
      "client",
    );
    const caseLawyer = normalizeRecipientOption(
      dataCase?.lawyers_relation,
      "lawyer",
    );

    const options = [caseClient, caseLawyer].filter(Boolean);
    setAssociatedOptions(options);
  }, [insideCase, dataCase]);

  // =====================
  // RENDER
  // =====================
  return (
    <Box
      sx={{
        padding: "20px",
        width: insideCase ? "100%" : "unset",
      }}>
      <Grid
        container
        sx={{
          height: heighProp || "760px",
          minHeight: heighProp || "760px",
          maxHeight: heighProp || "760px",
          background:
            "linear-gradient(140deg, rgba(244,248,255,0.72) 0%, rgba(255,255,255,0.88) 45%, rgba(235,242,252,0.78) 100%)",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          boxShadow: "0px 14px 32px -24px rgba(18, 36, 64, 0.75)",
          border: "1px solid rgba(13, 44, 84, 0.12)",
          overflow: "hidden",
        }}
        bgcolor="#fff">
        {/* SIDEBAR */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            p: 2,
            borderRight: { md: "1px solid #e7edf6" },
            height: "100%",
          }}>
          <SidebarMailbox
            loading={loadingMessages}
            filter={filter}
            setFilter={setFilter}
            hideInbox={insideCase}
            menuScopeFilter={menuScopeFilter}
            setMenuScopeFilter={setMenuScopeFilter}
            messages={displayedMessages}
            pagination={filter === "inbox" ? paginationInbox : paginationSent}
            openMessage={openMessage}
            setSelected={setSelected}
            onPageChange={handlePageChange}
            onRefreshMessages={getMessages}
          />
        </Grid>

        {/* DIVIDER */}
        {/* <Divider orientation="vertical" flexItem /> */}

        {/* CONTENIDO DERECHO */}
        <Grid
          item
          xs={12}
          md={8}
          container
          direction="column"
          sx={{ p: 2, gap: 2, height: "100%", overflow: "hidden" }}>
          {/* FILTROS */}
          <Grid item>
            <FiltersBar
              sharedFilters={sharedFilters}
              setSharedFilters={setSharedFilters}
              lawyers={[]}
              handleClear={() => {
                resetPagesOnFilterChange();
                setApplyToggle((t) => !t);
              }}
              handleApply={() => {
                resetPagesOnFilterChange();
                setApplyToggle((t) => !t);
              }}
            />
          </Grid>

          {/* CONTENIDO */}
          <Grid item sx={{ flex: 1, overflow: "auto", pr: 1 }}>
            {!selected && (
              <Typography variant="h5" color="text.secondary">
                Selecciona un mensaje
              </Typography>
            )}

            {selected && selected !== "compose" && (
              <MessageViewer
                selected={selected}
                filter={filter}
                insideCase={insideCase}
              />
            )}

            {selected === "compose" && (
              <MessageComposer
                {...{
                  companies,
                  sendMode,
                  setSendMode,
                  newMsg,
                  setNewMsg,
                  editorState,
                  onEditorChange: handleEditorChange,
                  clearEditor,
                  getHtmlFromEditor,
                  insideCase,
                  dataCase,
                  associatedOptions,
                  sendMessage,
                  isSending,
                  isTyping,
                }}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
